// Auth — Login + Signup (magic-link skipped per scope).
// Single screen template, two routed variants.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandPanel from "../components/auth/BrandPanel";
import GoogleIcon from "../components/auth/GoogleIcon";
import Wordmark from "../components/auth/Wordmark";
import { signInWithEmail, signUpAndCreateYard, signInOrSignUpWithGoogle } from "../lib/auth";
import { useIsMobile } from "../lib/useMediaQuery";
import "../styles/auth.css";

const AIc = {
  alert: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3" /><path d="M7 4.5V7.5M7 9.5V9.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
};

type Variant = "login" | "signup";

interface FormTopProps {
  variant: Variant;
  isMobile: boolean;
}

function FormTop({ variant, isMobile }: FormTopProps) {
  return (
    <div className="auth-form-top">
      {isMobile && <Wordmark mobile />}
      <span className="auth-form-top-link">
        {variant === "signup" && <>Already have an account? <Link to="/login">Log in</Link></>}
        {variant === "login" && <>New here? <Link to="/signup">Create a yard</Link></>}
      </span>
    </div>
  );
}

interface AuthShellProps {
  variant: Variant;
  children: React.ReactNode;
}

function AuthShell({ variant, children }: AuthShellProps) {
  const isMobile = useIsMobile();
  return (
    <div className={`auth ${isMobile ? "is-mobile" : "is-desktop"}`}>
      <div className="auth-frame">
        <BrandPanel variant={variant} />
        <div className="auth-form-panel">
          <FormTop variant={variant} isMobile={isMobile} />
          <div className="auth-form-body">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithEmail({ email, password });
      navigate("/dashboard");
    } catch (err) {
      setError("Email or password didn't match.");
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await signInOrSignUpWithGoogle();
      navigate(result.isNewUser ? "/onboarding" : "/dashboard");
    } catch (err) {
      const message = (err as Error)?.message;
      if (message === "MISSING_YARD_NAME") {
        navigate("/signup");
      } else {
        setError("Couldn't sign in with Google. Try email instead.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell variant="login">
      <form onSubmit={submit}>
        <div className="auth-heading">
          <h1>Welcome back</h1>
          <p>Log in to keep your daily streak going.</p>
        </div>

        <button className="auth-google" type="button" onClick={onGoogle} disabled={loading}>
          <GoogleIcon /> Continue with Google
        </button>
        <div className="auth-divider">or</div>

        <div className="auth-fields">
          <div className="auth-field">
            <label className="auth-field-label" htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="email"
              className="auth-input"
              placeholder="you@yardname.co.uk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="auth-field">
            <label className="auth-field-label" htmlFor="auth-pw">
              Password
              <Link to="/forgot-password" className="auth-field-extra">Forgot?</Link>
            </label>
            <div className="auth-input-wrap">
              <input
                id="auth-pw"
                type={showPw ? "text" : "password"}
                className={`auth-input ${error ? "has-error" : ""}`}
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button className="auth-input-suffix" type="button" onClick={() => setShowPw((s) => !s)}>
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
            {error && <span className="auth-error">{AIc.alert} {error}</span>}
          </div>
        </div>

        <button
          className={`auth-submit ${loading ? "is-loading" : ""}`}
          type="submit"
          disabled={loading}
        >
          {loading ? <><span className="auth-spinner"></span> Logging in…</> : <>Log in →</>}
        </button>

        <p className="auth-foot">
          New to Troughmate? <Link to="/signup">Create a yard</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [yardName, setYardName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pwError, setPwError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const strength = password.length === 0 ? 0 : password.length < 6 ? 1 : password.length < 9 ? 2 : 3;
  const strengthLabel = ["", "Too weak", "OK", "Strong"][strength];
  const strengthTone = ["", "weak", "medium", "strong"][strength];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPwError(null);
    if (password.length < 8) {
      setPwError("Use at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await signUpAndCreateYard({ email, password, yardName });
      navigate("/onboarding");
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/email-already-in-use") {
        setError("That email already has an account.");
      } else {
        setError("Couldn't create your yard. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async () => {
    if (!yardName.trim()) {
      setError("Pick a yard name first. Google signups still need one.");
      return;
    }
    if (!termsAccepted) {
      setError("Please accept the Terms and Privacy Policy first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await signInOrSignUpWithGoogle({ yardName });
      navigate(result.isNewUser ? "/onboarding" : "/dashboard");
    } catch (err) {
      setError("Couldn't sign up with Google. Try email instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell variant="signup">
      <form onSubmit={submit}>
        <div className="auth-heading">
          <h1>Create your yard account</h1>
          <p>Manual logging is free for unlimited horses. The W370 sensor (coming soon) is the paid upgrade.</p>
        </div>

        <button className="auth-google" type="button" onClick={onGoogle} disabled={loading}>
          <GoogleIcon /> Continue with Google
        </button>
        <div className="auth-divider">or</div>

        <div className="auth-fields">
          <div className="auth-field">
            <label className="auth-field-label" htmlFor="auth-email">Work email</label>
            <input
              id="auth-email"
              type="email"
              className={`auth-input ${error ? "has-error" : ""}`}
              placeholder="you@yardname.co.uk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {error && (
              <span className="auth-error">
                {AIc.alert} {error} <Link to="/login">Log in instead</Link>
              </span>
            )}
          </div>

          <div className="auth-field">
            <label className="auth-field-label" htmlFor="auth-pw">Password</label>
            <div className="auth-input-wrap">
              <input
                id="auth-pw"
                type={showPw ? "text" : "password"}
                className={`auth-input ${pwError ? "has-error" : ""}`}
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button className="auth-input-suffix" type="button" onClick={() => setShowPw((s) => !s)}>
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
            {strength > 0 && !pwError && (
              <div className="auth-strength">
                <div className="auth-strength-bar">
                  <span className={`auth-strength-seg ${strength >= 1 ? `on-${strengthTone}` : ""}`}></span>
                  <span className={`auth-strength-seg ${strength >= 2 ? `on-${strengthTone}` : ""}`}></span>
                  <span className={`auth-strength-seg ${strength >= 3 ? `on-${strengthTone}` : ""}`}></span>
                </div>
                <span>{strengthLabel}</span>
              </div>
            )}
            {pwError && <span className="auth-error">{AIc.alert} {pwError}</span>}
          </div>

          <div className="auth-field">
            <label className="auth-field-label" htmlFor="auth-yard">Yard name</label>
            <input
              id="auth-yard"
              type="text"
              className="auth-input"
              placeholder="e.g. Hollow Tree Livery"
              value={yardName}
              onChange={(e) => setYardName(e.target.value)}
              required
            />
            <span className="auth-help">You can invite teammates after signup.</span>
          </div>
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            marginBottom: 12,
            fontSize: 12,
            color: "var(--ink-3)",
            lineHeight: 1.55,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            style={{ marginTop: 2, flexShrink: 0 }}
            required
          />
          <span>
            I agree to the <Link to="/terms" target="_blank" rel="noopener">Terms &amp; Conditions</Link>{" "}
            and <Link to="/privacy" target="_blank" rel="noopener">Privacy Policy</Link>. I understand
            the Troughmate hydration status is a research-backed guide, not a vet diagnosis,
            and I will call my vet if I'm worried about a horse.
          </span>
        </label>

        <button
          className={`auth-submit ${loading ? "is-loading" : ""}`}
          type="submit"
          disabled={loading || !termsAccepted}
        >
          {loading ? <><span className="auth-spinner"></span> Creating yard…</> : <>Create yard →</>}
        </button>

        <p className="auth-foot">
          Already have an account? <Link to="/login">Log in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
